import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import { Button } from '../../components/ui/button';
import { LayoutDashboard, Building2, CreditCard, MessageSquare, Megaphone, LogOut, ShieldCheck, Menu, X, Settings } from 'lucide-react';

const navItems = [
  { path: '/platform/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/platform/tenants', label: 'Schools', icon: Building2 },
  { path: '/platform/modules', label: 'Feature Control', icon: Settings },
  { path: '/platform/revenue', label: 'Revenue', icon: CreditCard },
  { path: '/platform/messages', label: 'Messages', icon: MessageSquare },
  { path: '/platform/announcements', label: 'Announcements', icon: Megaphone },
];

const PlatformLayout = () => {
  const { admin, logout } = usePlatformAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (!admin) {
    navigate('/platform/login');
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/platform/login');
  };

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="platform-layout">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 border-r border-slate-800 transform transition-transform lg:translate-x-0 lg:static ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} data-testid="platform-sidebar">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-8 w-8 text-indigo-400" />
            <div>
              <span className="text-lg font-bold text-white block">Ariskool</span>
              <span className="text-xs text-slate-500">Platform Admin</span>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-1" data-testid="platform-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
                data-testid={`platform-nav-${item.label.toLowerCase()}`}
              >
                <Icon className="h-5 w-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-sm font-bold">SA</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-medium truncate">{admin?.full_name}</p>
              <p className="text-xs text-slate-500 truncate">{admin?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800" onClick={handleLogout} data-testid="platform-logout-btn">
            <LogOut className="h-4 w-4 mr-2" /> Sign Out
          </Button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800 px-6 py-4 flex items-center justify-between lg:justify-end">
          <button className="lg:hidden text-slate-400" onClick={() => setSidebarOpen(true)}>
            <Menu className="h-6 w-6" />
          </button>
          <p className="text-sm text-slate-500">Platform Console</p>
        </header>
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default PlatformLayout;
